<template lang="pug">
div
    feathers-vuex-find(service="bh-token-campaigns" :query="dataQuery" :fetch-query="fetchQuery" watch="query")
        v-card(slot-scope="{ items: campaigns, pagination: serverPagination, isFindPending }")
            v-card-title Token Campaign Manager
                v-spacer
                v-text-field(v-model="searchText" append-icon="mdi-magnify" label="Search by campaign name" single-line hide-details)
                v-spacer
                v-btn-toggle(v-model="statusFilter" mandatory color="primary")
                    v-btn(value="ACTIVE" small) Active
                    v-btn(value="PAUSED" small) Paused
                    v-btn(value="STOPPED" small) Stopped
                    v-btn(value="COMPLETED" small) Completed

            v-data-table(
                :headers="headers"
                :loading="isFindPending"
                :items="isFindPending ? [] : campaigns"
                :options.sync="pagination"
                :server-items-length="!serverPagination ? 0 : serverPagination.mostRecent.total"
                loading-text="Loading campaigns..."
                :footer-props="{ showCurrentPage: true, itemsPerPageOptions: [10, 25, 50] }"
                single-expand
                :expanded.sync="expanded"
                show-expand
            )
                template(v-slot:item.campaign_name="{ item }")
                    span {{ item.campaign_name && item.campaign_name.trim() ? item.campaign_name : `Campaign #${item.id}` }}

                template(v-slot:item.music_id="{ item }")
                    v-avatar(size="40" style="margin-right: 10px;")
                        img(v-if="item.music" :src="item.music.music_cover_thumb" :alt="item.music.title")
                    span(v-if="item.music") {{ item.music.title }}
                    span(v-else) Music \#{{ item.music_id }}

                template(v-slot:item.user_id="{ item }")
                    a(v-if="item.user" :href="`http://www.breakinghits.app/${item.user.url}`" target="_blank") 
                        | {{ item.user.fname }} {{ item.user.lname }}
                    span(v-else) User \#{{ item.user_id }}

                template(v-slot:item.total_budget="{ item }") 
                    v-chip(small color="blue" text-color="white") {{ item.total_budget }} 🪙

                template(v-slot:item.tokens_spent="{ item }") 
                    v-chip(small color="red" text-color="white") {{ item.tokens_spent }} 🪙

                template(v-slot:item.tokens_reserved="{ item }") 
                    v-chip(small color="orange" text-color="white") {{ item.tokens_reserved }} 🪙

                template(v-slot:item.progress="{ item }")
                    v-progress-linear(
                        :value="(item.tokens_spent / item.total_budget) * 100"
                        height="20"
                        rounded
                        :color="getProgressColor(item)"
                    )
                        template(v-slot:default="{ value }")
                            strong {{ Math.ceil(value) }}%

                template(v-slot:item.status="{ item }")
                    v-chip(
                        small
                        :color="getStatusColor(item.status)"
                        text-color="white"
                    ) {{ item.status }}

                template(v-slot:item.createdAt="{ item }") {{ item.date_created | dateFormat }}

                template(v-slot:item.actions="{ item }")
                    v-btn(icon small color="primary" @click="viewParticipants(item)" title="View Participants")
                        v-icon mdi-account-group
                    v-btn(v-if="item.status === 'ACTIVE'" icon small color="orange" @click="pauseCampaign(item)" title="Pause")
                        v-icon mdi-pause
                    v-btn(v-if="item.status === 'PAUSED'" icon small color="green" @click="resumeCampaign(item)" title="Resume")
                        v-icon mdi-play
                    v-btn(v-if="item.status !== 'STOPPED' && item.status !== 'COMPLETED'" icon small color="red" @click="confirmStopCampaign(item)" title="Stop & Refund")
                        v-icon mdi-stop
                    v-btn(icon small color="blue" @click="viewDetails(item)" title="View Details")
                        v-icon mdi-eye

                template(v-slot:expanded-item="{ headers, item }")
                    td(:colspan="headers.length" style="padding: 20px;")
                        v-row
                            v-col(cols="12" md="6")
                                v-card
                                    v-card-title Budget & Stats
                                    v-card-text
                                        v-simple-table
                                            tbody
                                                tr
                                                    td.font-weight-bold Total Budget
                                                    td {{ item.total_budget }} tokens
                                                tr
                                                    td.font-weight-bold Tokens Spent
                                                    td {{ item.tokens_spent }} tokens
                                                tr
                                                    td.font-weight-bold Tokens Reserved
                                                    td {{ item.tokens_reserved }} tokens
                                                tr
                                                    td.font-weight-bold Feedback Count
                                                    td {{ item.feedback_count }}
                                                tr(v-if="item.target_feedback_count")
                                                    td.font-weight-bold Target Feedback
                                                    td {{ item.target_feedback_count }}
                            
                            v-col(cols="12" md="6")
                                v-card
                                    v-card-title Campaign Info
                                    v-card-text
                                        v-simple-table
                                            tbody
                                                tr
                                                    td.font-weight-bold Campaign Type
                                                    td 
                                                        v-chip(small :color="getCampaignTypeColor(item.campaign_type)" text-color="white") {{ item.campaign_type }}
                                                        v-chip.ml-2(v-if="item.metadata && item.metadata.is_split_campaign" x-small color="deep-orange" text-color="white") SPLIT
                                                tr(v-if="item.metadata && item.metadata.original_campaign_name")
                                                    td.font-weight-bold Original Campaign
                                                    td {{ item.metadata.original_campaign_name }}
                                                tr
                                                    td.font-weight-bold Allocation Types
                                                    td 
                                                        feathers-vuex-find(service="bh-campaign-allocations" :query="{ campaign_id: item.id }" :fetch-query="{ campaign_id: item.id }")
                                                            template(v-slot="{ items: allocations }")
                                                                v-chip.mr-1(
                                                                    v-for="type in getUniqueAllocationTypes(allocations)"
                                                                    :key="type"
                                                                    x-small
                                                                    :color="getAllocationTypeColor(type)"
                                                                    text-color="white"
                                                                ) {{ type }}
                                                tr
                                                    td.font-weight-bold Start Date
                                                    td {{ item.start_date | dateFormat }}
                                                tr
                                                    td.font-weight-bold End Date
                                                    td {{ item.end_date ? (item.end_date | dateFormat) : 'No end date' }}
                                                tr
                                                    td.font-weight-bold Created
                                                    td {{ item.createdAt | dateFormat }}
                                                tr
                                                    td.font-weight-bold Last Updated
                                                    td {{ item.updatedAt | dateFormat }}

            v-dialog(v-model="stopDialog" max-width="500px")
                v-card
                    v-card-title.headline Confirm Stop Campaign
                    v-card-text 
                        p Are you sure you want to stop this campaign?
                        p.red--text This will refund unused tokens to the user and cannot be undone.
                    v-card-actions
                        v-spacer
                        v-btn(color="grey" text @click="stopDialog = false") Cancel
                        v-btn(color="red" text @click="stopCampaignConfirmed") Stop & Refund

            v-dialog(v-model="participantsDialog" max-width="1200px" scrollable)
                v-card(v-if="selectedCampaign")
                    v-card-title
                        span Campaign Participants: {{ selectedCampaign.campaign_name || `Campaign #${selectedCampaign.id}` }}
                        v-spacer
                        v-btn(icon @click="loadParticipants")
                            v-icon mdi-refresh
                        v-btn(icon @click="participantsDialog = false")
                            v-icon mdi-close
                    
                    v-card-subtitle
                        v-row(no-gutters)
                            v-col(cols="auto")
                                v-chip.mr-2(small color="blue" text-color="white") Total: {{ participantStats.total }}
                            v-col(cols="auto")
                                v-chip.mr-2(small color="green" text-color="white") Done: {{ participantStats.done }}
                            v-col(cols="auto")
                                v-chip.mr-2(small color="orange" text-color="white") Pending: {{ participantStats.pending }}
                            v-col(cols="auto")
                                v-chip.mr-2(small color="purple" text-color="white") Avg Engagement: {{ participantStats.avgEngagement.toFixed(1) }}
                            v-col(cols="auto")
                                v-chip(small color="amber" text-color="white") Fast Responders: {{ participantStats.fastResponders }}
                    
                    v-card-text(style="max-height: 600px;")
                        v-row.mb-4
                            v-col(cols="12" md="6")
                                v-chip-group(v-model="filterStatus" mandatory)
                                    v-chip(filter value="all" small) All
                                    v-chip(filter value="done" small) Done
                                    v-chip(filter value="pending" small) Pending
                            v-col(cols="12" md="6")
                                v-chip-group(v-model="filterSpeed" mandatory)
                                    v-chip(filter value="all" small) All Speeds
                                    v-chip(filter value="fast" small) Fast
                                    v-chip(filter value="average" small) Average
                                    v-chip(filter value="slow" small) Slow
                        
                        v-data-table(
                            :headers="participantHeaders"
                            :items="filteredParticipants"
                            :loading="participantsLoading"
                            loading-text="Loading participants..."
                            dense
                        )
                            template(v-slot:item.name="{ item }")
                                span {{ item.fname }} {{ item.lname }}
                            
                            template(v-slot:item.status="{ item }")
                                v-chip(
                                    x-small
                                    :color="item.status === 'Done' ? 'green' : 'orange'"
                                    text-color="white"
                                ) {{ item.status }}
                            
                            template(v-slot:item.responder_speed="{ item }")
                                v-chip(
                                    x-small
                                    :color="getSpeedColor(item.responder_speed)"
                                    text-color="white"
                                )
                                    v-icon(left x-small) {{ getSpeedIcon(item.responder_speed) }}
                                    | {{ item.responder_speed }}
                            
                            template(v-slot:item.engagement_score="{ item }")
                                v-progress-linear(
                                    :value="item.engagement_score"
                                    height="20"
                                    rounded
                                    :color="getEngagementColor(item.engagement_score)"
                                )
                                    strong {{ item.engagement_score ? item.engagement_score.toFixed(1) : '0' }}
                            
                            template(v-slot:item.engagement_trend="{ item }")
                                v-icon(
                                    v-if="item.engagement_trend > 0"
                                    small
                                    color="green"
                                ) mdi-trending-up
                                v-icon(
                                    v-else-if="item.engagement_trend < 0"
                                    small
                                    color="red"
                                ) mdi-trending-down
                                v-icon(
                                    v-else
                                    small
                                    color="grey"
                                ) mdi-minus
                            
                            template(v-slot:item.answers="{ item }")
                                v-btn(
                                    v-if="item.status === 'Done' && item.survey_data"
                                    icon
                                    small
                                    color="blue"
                                    @click="viewSurveyAnswers(item)"
                                    title="View Answers"
                                )
                                    v-icon mdi-eye
                    
                    v-card-actions
                        v-spacer
                        v-btn(
                            v-if="participantStats.pending > 0"
                            color="primary"
                            @click="sendReminders"
                            :loading="sendingReminders"
                        )
                            v-icon(left) mdi-send
                            | Send Reminders ({{ participantStats.pending }})

            v-dialog(v-model="answersDialog" max-width="600px" scrollable)
                v-card(v-if="selectedParticipant")
                    v-card-title Survey Answers - {{ selectedParticipant.fname }} {{ selectedParticipant.lname }}
                        v-spacer
                        v-btn(icon @click="answersDialog = false")
                            v-icon mdi-close
                    v-card-text
                        v-list(dense)
                            template(v-for="(value, key) in selectedParticipant.survey_data")
                                v-list-item(:key="key")
                                    v-list-item-content
                                        v-list-item-subtitle.font-weight-bold.mb-1 {{ key.toUpperCase() }}
                                        v-list-item-title.text-wrap {{ value }}
                                v-divider

            v-snackbar(top v-model="successMessage" color="success") {{ successText }}
            v-snackbar(top v-model="error.show" color="red") {{ error.message }}
</template>

<script>
import moment from 'moment';
import feathersClient from '@/plugins/feathers-client';

export default {
    name: 'CampaignManager',
    data() {
        return {
            statusFilter: 'ACTIVE',
            searchText: '',
            expanded: [],
            stopDialog: false,
            selectedCampaign: null,
            participantsDialog: false,
            participants: [],
            participantsLoading: false,
            answersDialog: false,
            selectedParticipant: null,
            filterStatus: 'all',
            filterSpeed: 'all',
            sendingReminders: false,
            participantHeaders: [
                { text: 'Name', value: 'name' },
                { text: 'Status', value: 'status', align: 'center' },
                { text: 'Speed', value: 'responder_speed', align: 'center' },
                { text: 'Engagement Score', value: 'engagement_score', align: 'center', width: 150 },
                { text: 'Trend', value: 'engagement_trend', align: 'center' },
                { text: 'Answers', value: 'answers', align: 'center', sortable: false }
            ],
            successMessage: false,
            successText: '',
            error: {
                show: false,
                message: ''
            },
            headers: [
                {
                    text: 'ID',
                    align: 'start',
                    sortable: true,
                    value: 'id',
                    width: 80
                },
                {
                    text: 'Campaign Name',
                    align: 'start',
                    sortable: true,
                    value: 'campaign_name',
                    width: 180
                },
                {
                    text: 'Music',
                    align: 'start',
                    sortable: false,
                    value: 'music_id',
                    width: 250
                },
                {
                    text: 'User',
                    align: 'start',
                    sortable: false,
                    value: 'user_id',
                    width: 150
                },
                {
                    text: 'Budget',
                    align: 'center',
                    sortable: true,
                    value: 'total_budget',
                    width: 100
                },
                {
                    text: 'Spent',
                    align: 'center',
                    sortable: true,
                    value: 'tokens_spent',
                    width: 100
                },
                {
                    text: 'Reserved',
                    align: 'center',
                    sortable: true,
                    value: 'tokens_reserved',
                    width: 100
                },
                {
                    text: 'Progress',
                    align: 'center',
                    sortable: false,
                    value: 'progress',
                    width: 120
                },
                {
                    text: 'Status',
                    align: 'center',
                    sortable: true,
                    value: 'status',
                    width: 120
                },
                {
                    text: 'Created',
                    align: 'end',
                    sortable: true,
                    value: 'createdAt',
                    width: 120
                },
                {
                    text: 'Actions',
                    align: 'end',
                    sortable: false,
                    value: 'actions',
                    width: 150
                },
                {
                    text: '',
                    value: 'data-table-expand'
                }
            ],
            pagination: {
                page: 1,
                itemsPerPage: 10,
                sortBy: ['createdAt'],
                sortDesc: [true]
            }
        };
    },
    computed: {
        dataQuery() {
            const query = {
                status: this.statusFilter,
                $limit: this.pagination.itemsPerPage,
                $skip: (this.pagination.page - 1) * this.pagination.itemsPerPage
            };

            if (this.searchText) {
                query.campaign_name = {
                    $like: `%${this.searchText}%`
                };
            }

            if (this.pagination.sortBy && this.pagination.sortBy.length > 0) {
                query.$sort = {};
                this.pagination.sortBy.forEach((field, index) => {
                    query.$sort[field] = this.pagination.sortDesc[index] ? -1 : 1;
                });
            }

            return query;
        },
        fetchQuery() {
            return Object.assign({}, this.dataQuery);
        },
        participantStats() {
            if (this.participants.length === 0) {
                return { total: 0, done: 0, pending: 0, avgEngagement: 0, fastResponders: 0 }
            }
            const done = this.participants.filter(p => p.status === 'Done').length
            const pending = this.participants.filter(p => p.status === 'Pending').length
            const avgEngagement = this.participants.reduce((sum, p) => sum + (p.engagement_score || 0), 0) / this.participants.length
            const fastResponders = this.participants.filter(p => p.responder_speed === 'Fast').length
            return {
                total: this.participants.length,
                done,
                pending,
                avgEngagement,
                fastResponders
            }
        },
        filteredParticipants() {
            let filtered = this.participants
            
            // Filter by status
            if (this.filterStatus !== 'all') {
                const status = this.filterStatus === 'done' ? 'Done' : 'Pending'
                filtered = filtered.filter(p => p.status === status)
            }
            
            // Filter by speed
            if (this.filterSpeed !== 'all') {
                const speed = this.filterSpeed.charAt(0).toUpperCase() + this.filterSpeed.slice(1)
                filtered = filtered.filter(p => p.responder_speed === speed)
            }
            
            return filtered
        }
    },
    methods: {
        getStatusColor(status) {
            switch (status) {
                case 'ACTIVE': return 'green';
                case 'PAUSED': return 'orange';
                case 'STOPPED': return 'red';
                case 'COMPLETED': return 'blue';
                default: return 'grey';
            }
        },
        getProgressColor(item) {
            const progress = (item.tokens_spent / item.total_budget) * 100;
            if (progress >= 90) return 'red';
            if (progress >= 70) return 'orange';
            return 'green';
        },
        getCampaignTypeColor(type) {
            switch (type) {
                case 'PUBLIC': return 'blue';
                case 'GROUP': return 'purple';
                case 'INDIVIDUAL': return 'teal';
                case 'CATEGORY': return 'indigo';
                default: return 'grey';
            }
        },
        getAllocationTypeColor(type) {
            switch (type) {
                case 'PUBLIC': return 'blue';
                case 'GROUP': return 'purple';
                case 'INDIVIDUAL': return 'teal';
                case 'CATEGORY': return 'indigo';
                default: return 'grey';
            }
        },
        getUniqueAllocationTypes(allocations) {
            if (!allocations || allocations.length === 0) return [];
            const types = new Set(allocations.map(a => a.target_type));
            return Array.from(types);
        },
        async pauseCampaign(item) {
            try {
                await this.$store.dispatch('bh-token-campaigns/patch', [
                    item.id,
                    { status: 'PAUSED' }
                ]);
                this.successText = 'Campaign paused successfully';
                this.successMessage = true;
            } catch (error) {
                this.error.message = `Error pausing campaign: ${error.message}`;
                this.error.show = true;
            }
        },
        async resumeCampaign(item) {
            try {
                await this.$store.dispatch('bh-token-campaigns/patch', [
                    item.id,
                    { status: 'ACTIVE' }
                ]);
                this.successText = 'Campaign resumed successfully';
                this.successMessage = true;
            } catch (error) {
                this.error.message = `Error resuming campaign: ${error.message}`;
                this.error.show = true;
            }
        },
        confirmStopCampaign(item) {
            this.selectedCampaign = item;
            this.stopDialog = true;
        },
        async stopCampaignConfirmed() {
            try {
                await feathersClient.service('api/token-campaigns').patch(
                    this.selectedCampaign.id,
                    { status: 'STOPPED' }
                );
                this.successText = 'Campaign stopped and unused tokens refunded';
                this.successMessage = true;
                this.stopDialog = false;
                this.selectedCampaign = null;
            } catch (error) {
                this.error.message = `Error stopping campaign: ${error.message}`;
                this.error.show = true;
            }
        },
        viewDetails(item) {
            this.expanded = this.expanded.includes(item) ? [] : [item];
        },
        async viewParticipants(item) {
            this.selectedCampaign = item;
            this.participantsDialog = true;
            await this.loadParticipants();
        },
        viewSurveyAnswers(participant) {
            this.selectedParticipant = participant;
            this.answersDialog = true;
        },
        async loadParticipants() {
            if (!this.selectedCampaign) return;
            
            this.participantsLoading = true;
            try {
                const response = await feathersClient.service('api/token-campaigns').find({
                    query: {
                        action: 'get-participants',
                        campaign_id: this.selectedCampaign.id,
                        trend_period: 1
                    }
                });
                
                this.participants = response.participants || [];
            } catch (error) {
                this.error.message = `Error loading participants: ${error.message}`;
                this.error.show = true;
                this.participants = [];
            } finally {
                this.participantsLoading = false;
            }
        },
        async sendReminders() {
            if (!this.selectedCampaign) return;
            
            this.sendingReminders = true;
            try {
                await feathersClient.service('api/token-campaigns').create({
                    action: 'send-reminders',
                    campaign_id: this.selectedCampaign.id
                });
                
                this.successText = 'Reminders sent successfully';
                this.successMessage = true;
            } catch (error) {
                this.error.message = `Error sending reminders: ${error.message}`;
                this.error.show = true;
            } finally {
                this.sendingReminders = false;
            }
        },
        getSpeedColor(speed) {
            switch (speed) {
                case 'Fast': return 'green';
                case 'Average': return 'orange';
                case 'Slow': return 'red';
                default: return 'grey';
            }
        },
        getSpeedIcon(speed) {
            switch (speed) {
                case 'Fast': return 'mdi-run-fast';
                case 'Average': return 'mdi-walk';
                case 'Slow': return 'mdi-tortoise';
                default: return 'mdi-help';
            }
        },
        getEngagementColor(score) {
            if (score >= 80) return 'green';
            if (score >= 60) return 'light-green';
            if (score >= 40) return 'orange';
            if (score >= 20) return 'deep-orange';
            return 'red';
        }
    },
    filters: {
        dateFormat(value) {
            if (!value) return '';
            return moment(value).format('MMM DD, YYYY HH:mm');
        },
        genreParser(value, genres) {
            if (!value || !genres) return '';
            const genre = genres.find(g => g.id === value);
            return genre ? genre.genre : value;
        }
    }
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__expanded__content {
    box-shadow: none !important;
}
</style>
